import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Start with loading true to wait for localStorage restoration
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const verifyToken = useCallback(async () => {
    // Verify token in background (non-blocking)
    try {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 3000)
      );
      
      const response = await Promise.race([
        api.get('/auth/verify'),
        timeoutPromise
      ]);
      
      if (response.data.success) {
        // Check if user status is INACTIVE
        const userData = response.data.user;
        if (userData.isActiveValue === 0 || userData.status === 'INACTIVE') {
          // User is inactive, logout immediately
          logout();
          return;
        }
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      } else {
        logout();
      }
    } catch (error) {
      // Check if error is due to inactive account
      if (error.response?.status === 403 && error.response?.data?.message === 'ACCOUNT_INACTIVE') {
        // Account is inactive, logout immediately
        logout();
        return;
      }
      // For network errors or other errors, don't logout immediately (might be temporary)
      // Only logout on actual auth errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
      }
    }
  }, [logout]);

  useEffect(() => {
    // Restore from localStorage on mount
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      // Restore user from localStorage immediately (no verification)
      try {
        const parsedUser = JSON.parse(savedUser);
        // Check if saved user status is INACTIVE
        if (parsedUser.isActiveValue === 0 || parsedUser.status === 'INACTIVE') {
          // User is inactive, logout immediately
          logout();
          setLoading(false);
          return;
        }
        setUser(parsedUser);
        // Verify token in background to ensure user is still active
        verifyToken();
      } catch (e) {
        // If parsing fails, clear invalid data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
    }
    // Mark loading as complete after restoration attempt
    setLoading(false);
  }, [logout, verifyToken]);

  // Periodically verify user status to catch admin status changes
  useEffect(() => {
    if (!user) return;

    // Verify user status every 30 seconds to catch admin status changes
    const intervalId = setInterval(() => {
      verifyToken();
    }, 30000); // Check every 30 seconds

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [user, verifyToken]);

  const persistSession = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      if (response.data.success) {
        persistSession(response.data.token, response.data.user);
        return { success: true };
      }
      // Return consistent error message for invalid credentials
      return { 
        success: false, 
        message: response.data.message || 'Invalid credentials. Please check your email and password.' 
      };
    } catch (error) {
      // Handle inactive account error (403 with ACCOUNT_INACTIVE message)
      if (error.response?.status === 403 && error.response?.data?.message === 'ACCOUNT_INACTIVE') {
        return {
          success: false,
          message: 'ACCOUNT_INACTIVE',
          error: error.response?.data?.error || 'Your account is inactive. Please contact support for assistance.',
        };
      }
      // Handle 401/403 errors as invalid credentials
      if (error.response?.status === 401 || error.response?.status === 403) {
        return {
          success: false,
          message: 'Invalid credentials. Please check your email and password.',
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || 'Invalid credentials. Please check your email and password.',
      };
    }
  };

  const register = async (email, password, firstName, lastName) => {
    try {
      const response = await api.post('/auth/register', {
        email,
        password,
        firstName,
        lastName,
      });
      if (response.data.success) {
        persistSession(response.data.token, response.data.user);
        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      const validationMessage = error.response?.data?.errors?.[0]?.msg;
      return {
        success: false,
        message: error.response?.data?.message || validationMessage || 'Registration failed',
      };
    }
  };

  const sendOtp = async (email, password, firstName, lastName) => {
    try {
      const response = await api.post('/otp/send-otp', {
        email,
        password,
        firstName,
        lastName,
      });
      return {
        success: response.data.success,
        message: response.data.message || 'OTP sent successfully',
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send OTP',
      };
    }
  };

  const verifyOtpAndRegister = async (email, otp) => {
    try {
      const response = await api.post('/otp/verify-otp-register', {
        email,
        otp,
      });
      if (response.data.success) {
        persistSession(response.data.token, response.data.user);
        return { success: true };
      }
      return { success: false, message: response.data.message };
    } catch (error) {
      const validationMessage = error.response?.data?.errors?.[0]?.msg;
      return {
        success: false,
        message: error.response?.data?.message || validationMessage || 'OTP verification failed',
      };
    }
  };


  const loginWithGoogle = async (credential) => {
    try {
      const response = await api.post('/auth/google', { credential });
      if (response.data.success) {
        persistSession(response.data.token, response.data.user);
        return { success: true };
      }
      return { success: false, message: response.data.message || 'Google sign-in failed' };
    } catch (error) {
      // Handle inactive account error (403 with ACCOUNT_INACTIVE message)
      if (error.response?.status === 403 && error.response?.data?.message === 'ACCOUNT_INACTIVE') {
        return {
          success: false,
          message: 'ACCOUNT_INACTIVE',
          error: error.response?.data?.error || 'Your account is inactive. Please contact support for assistance.',
        };
      }
      return {
        success: false,
        message: error.response?.data?.message || 'Google sign-in failed',
      };
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    sendOtp,
    verifyOtpAndRegister,
    loginWithGoogle,
    logout,
    updateUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

